import React, { useState, useEffect } from 'react';
import { Button, Drawer, Form, Input, Select, Icon, Tooltip, Popconfirm } from 'antd';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import uid from 'uid';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import * as settingActions from '~/containers/Settings/actions';

var Option = Select.Option;

var SectionCard = styled.div`
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  padding: 20px 24px;
  margin-bottom: 16px;

  .ant-form-item { margin-bottom: 12px; }
  .ant-form-item:last-child { margin-bottom: 0; }
  .ant-form-item-label { line-height: 1.5; padding-bottom: 4px; }
  .ant-form-item-label label { font-size: 13px; font-weight: 500; color: #595959; }
  .ant-input, .ant-select-selection { border-radius: 6px; height: 40px; font-size: 14px; }
  .ant-select-selection__rendered { line-height: 38px; }
`;

var SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #8c8c8c;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
`;

var StepCard = styled.div`
  background: #fff;
  border: 1px solid ${function(p) { return p.isDragging ? '#1890ff' : '#f0f0f0'; }};
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 10px;
  transition: all 0.15s;
  box-shadow: ${function(p) { return p.isDragging ? '0 4px 16px rgba(24,144,255,0.15)' : 'none'; }};

  &:hover { border-color: #d9d9d9; }

  .ant-form-item { margin-bottom: 8px; }
  .ant-form-item:last-child { margin-bottom: 0; }
  .ant-input, .ant-select-selection { border-radius: 6px; height: 36px; font-size: 13px; }
  .ant-select-selection__rendered { line-height: 34px; }
`;

var StepHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

var StepNumber = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

var StepBadge = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${function(p) { return p.color || '#1890ff'; }};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
`;

var StepTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #262626;
`;

var DragHandle = styled.div`
  display: flex;
  align-items: center;
  color: #d9d9d9;
  cursor: grab;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.15s;

  &:hover { color: #1890ff; background: #e6f7ff; }
`;

var StepActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

var ScriptArea = styled.div`
  margin-top: 10px;
`;

var ScriptLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 500;
  color: #8c8c8c;
  margin-bottom: 4px;
`;

var EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #bfbfbf;
`;

var AddStepButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px;
  border: 2px dashed #d9d9d9;
  border-radius: 10px;
  color: #8c8c8c;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  margin-top: 4px;

  &:hover {
    border-color: #1890ff;
    color: #1890ff;
    background: #e6f7ff;
  }
`;

var FooterBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 24px;
  background: #fff;
  border-top: 1px solid #f0f0f0;
  z-index: 10;

  .ant-btn {
    height: 44px;
    font-size: 15px;
    padding: 0 28px;
    border-radius: 8px;
  }
`;

var StepCount = styled.div`
  font-size: 12px;
  color: #8c8c8c;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

/**
 * CadenceDrawer - shared drawer for Prospecção and Social Selling cadences.
 *
 * Props:
 * - visible, onClose, selectedCadence
 * - settingsKey: 'prospecting.cadence' | 'socialSelling.cadence'
 * - activityTypeField: 'select' | 'input' (default 'input')
 * - activityOptions: array of { value, label } for select mode
 * - color: theme color for step badges
 */
var CadenceDrawer = Form.create()(function(props) {
  var visible = props.visible;
  var onClose = props.onClose;
  var form = props.form;
  var selectedCadence = props.selectedCadence;
  var settingsKey = props.settingsKey;
  var activityTypeField = props.activityTypeField || 'input';
  var activityOptions = props.activityOptions || [];
  var color = props.color || '#1890ff';

  var nameState = useState(null);
  var cadenceName = nameState[0];
  var setCadenceName = nameState[1];

  var dataState = useState([]);
  var cadenceData = dataState[0];
  var setCadenceData = dataState[1];

  var dispatch = useDispatch();

  useEffect(function() {
    if (selectedCadence) {
      var d = JSON.parse(selectedCadence.value);
      setCadenceName(d.name);
      setCadenceData(d.data || []);
    } else {
      setCadenceName(null);
      setCadenceData([]);
      form.resetFields();
    }
  }, [selectedCadence]);

  var handleSave = function() {
    form.validateFields(function(err, values) {
      if (!err) {
        var newCadence = { name: values.name, data: values.data };
        var data = JSON.stringify(newCadence);
        if (selectedCadence && selectedCadence.id !== undefined) {
          dispatch(settingActions.updateSetting({ data: { key: settingsKey, value: data }, id: selectedCadence.id }));
        } else {
          dispatch(settingActions.createSetting({ data: { key: settingsKey, value: data } }));
        }
        onClose();
      }
    });
  };

  var addStep = function() {
    var newData = [].concat(cadenceData, [{ uid: uid(), status: 'pending' }]);
    setCadenceData(newData);
    form.setFieldsValue({ data: newData });
  };

  var deleteStep = function(index) {
    var newData = cadenceData.filter(function(_, i) { return i !== index; });
    setCadenceData(newData);
    form.setFieldsValue({ data: newData });
  };

  var handleDragEnd = function(result) {
    if (!result.destination) return;
    var updated = Array.from(cadenceData);
    var removed = updated.splice(result.source.index, 1);
    updated.splice(result.destination.index, 0, removed[0]);
    setCadenceData(updated);
    form.setFieldsValue({ data: updated });
  };

  var updateDataField = function(index, field, value) {
    var data = Array.isArray(form.getFieldValue('data')) ? form.getFieldValue('data') : [];
    if (!data[index]) data[index] = {};
    data[index][field] = value;
    form.setFieldsValue({ data: data });
  };

  return React.createElement(Drawer, {
    title: cadenceName ? 'Editar Cadência' : 'Criar Cadência',
    width: 720,
    visible: visible,
    onClose: onClose,
    zIndex: 1020,
    destroyOnClose: true,
    bodyStyle: { padding: '20px 24px 72px', background: '#fafafa' }
  },
    React.createElement(Form, { layout: 'vertical', hideRequiredMark: true },

      React.createElement(SectionLabel, null,
        React.createElement(Icon, { type: 'edit', style: { fontSize: 14 } }),
        'Informações'
      ),
      React.createElement(SectionCard, null,
        React.createElement(Form.Item, { label: 'Nome da cadência' },
          form.getFieldDecorator('name', {
            initialValue: cadenceName,
            rules: [{ required: true, message: 'Informe o nome da cadência' }],
          })(React.createElement(Input, { placeholder: 'Ex: Cadência padrão de prospecção' }))
        )
      ),

      React.createElement(SectionLabel, null,
        React.createElement(Icon, { type: 'ordered-list', style: { fontSize: 14 } }),
        'Etapas da Cadência'
      ),

      React.createElement(StepCount, null,
        React.createElement(Icon, { type: 'info-circle', style: { fontSize: 12 } }),
        cadenceData.length === 0
          ? 'Nenhuma etapa adicionada'
          : cadenceData.length + ' etapa' + (cadenceData.length > 1 ? 's' : '')
      ),

      form.getFieldDecorator('data', { initialValue: cadenceData })(
        React.createElement('span', { style: { display: 'none' } })
      ),

      cadenceData.length === 0
        ? React.createElement(EmptyState, null,
            React.createElement(Icon, { type: 'inbox', style: { fontSize: 40, marginBottom: 10, display: 'block' } }),
            React.createElement('div', { style: { fontSize: 14, marginBottom: 4 } }, 'Nenhuma etapa'),
            React.createElement('div', { style: { fontSize: 12 } }, 'Adicione etapas para definir a sequência de atividades')
          )
        : React.createElement(DragDropContext, { onDragEnd: handleDragEnd },
            React.createElement(Droppable, { droppableId: 'cadenceSteps' }, function(provided) {
              return React.createElement('div', Object.assign({ ref: provided.innerRef }, provided.droppableProps),
                cadenceData.map(function(item, index) {
                  return React.createElement(Draggable, {
                    key: item.uid, draggableId: String(item.uid), index: index
                  }, function(provided, snapshot) {
                    return React.createElement(StepCard, Object.assign({
                      ref: provided.innerRef,
                      isDragging: snapshot.isDragging
                    }, provided.draggableProps),

                      React.createElement(StepHeader, null,
                        React.createElement(StepNumber, null,
                          React.createElement(DragHandle, provided.dragHandleProps,
                            React.createElement(Icon, { type: 'menu', style: { fontSize: 14 } })
                          ),
                          React.createElement(StepBadge, { color: color }, index + 1),
                          React.createElement(StepTitle, null, 'Etapa ' + (index + 1))
                        ),
                        React.createElement(StepActions, null,
                          React.createElement(Popconfirm, {
                            title: 'Remover esta etapa?',
                            okText: 'Sim', cancelText: 'Não',
                            zIndex: 1030,
                            onConfirm: function() { deleteStep(index); }
                          },
                            React.createElement(Button, {
                              type: 'link', size: 'small',
                              style: { color: '#ff4d4f', fontSize: 12 }
                            },
                              React.createElement(Icon, { type: 'delete' }),
                              ' Remover'
                            )
                          )
                        )
                      ),

                      React.createElement('div', { style: { display: 'flex', gap: 12 } },
                        React.createElement('div', { style: { width: 120 } },
                          React.createElement(Form.Item, { label: 'Dia', style: { marginBottom: 0 } },
                            form.getFieldDecorator('day_' + index, {
                              initialValue: item.day,
                              rules: [{ required: true, message: 'Obrigatório' }],
                            })(React.createElement(Input, {
                              type: 'number', placeholder: 'Dia',
                              addonBefore: React.createElement(Icon, { type: 'calendar' }),
                              onChange: function(e) { updateDataField(index, 'day', parseInt(e.target.value)); }
                            }))
                          )
                        ),
                        React.createElement('div', { style: { flex: 1 } },
                          activityTypeField === 'select'
                            ? React.createElement(Form.Item, { label: 'Atividade', style: { marginBottom: 0 } },
                                form.getFieldDecorator('type_' + index, {
                                  initialValue: item.type,
                                  rules: [{ required: true, message: 'Obrigatório' }],
                                })(React.createElement(Select, {
                                  placeholder: 'Selecione a atividade',
                                  onChange: function(val) { updateDataField(index, 'type', val); }
                                },
                                  activityOptions.map(function(opt) {
                                    return React.createElement(Option, { key: opt.value, value: opt.value }, opt.label);
                                  })
                                ))
                              )
                            : React.createElement(Form.Item, { label: 'Atividade', style: { marginBottom: 0 } },
                                form.getFieldDecorator('type_' + index, {
                                  initialValue: item.type,
                                  rules: [{ required: true, message: 'Obrigatório' }],
                                })(React.createElement(Input, {
                                  placeholder: 'Ex: Enviar mensagem, Ligar...',
                                  onChange: function(e) { updateDataField(index, 'type', e.target.value); }
                                }))
                              )
                        )
                      ),

                      React.createElement(ScriptArea, null,
                        React.createElement(ScriptLabel, null,
                          React.createElement(Icon, { type: 'edit', style: { fontSize: 11 } }),
                          'Script / Roteiro'
                        ),
                        React.createElement(Input.TextArea, {
                          rows: 2,
                          autoSize: { minRows: 2, maxRows: 6 },
                          placeholder: 'Ex: "Olá [nome], tudo bem? Vi que sua empresa trabalha com..."',
                          defaultValue: item.script || '',
                          style: { borderRadius: 6, fontSize: 13, background: '#fafafa' },
                          onChange: function(e) { updateDataField(index, 'script', e.target.value); }
                        })
                      )
                    );
                  });
                }),
                provided.placeholder
              );
            })
          ),

      React.createElement(AddStepButton, { onClick: addStep },
        React.createElement(Icon, { type: 'plus' }),
        'Adicionar Etapa'
      )
    ),

    React.createElement(FooterBar, null,
      React.createElement(Button, { onClick: onClose }, 'Cancelar'),
      React.createElement(Button, { onClick: handleSave, type: 'primary', icon: 'save' },
        selectedCadence ? 'Salvar' : 'Criar'
      )
    )
  );
});

export default CadenceDrawer;
